import type { ReactNode } from 'react';
import { useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import { useAlert } from '@inbody/core';

import { Loading } from '../loading';

import { useMyMenusQuery } from '@/pages/user-permission/api';

import { useCurrentMenuId } from '@/shared/hooks';

/**
 * 데이터 조회 페이지 접근 가드.
 * 권한은 Keycloak 역할이 아니라 /me/menus(메뉴별)로 정해지므로, 현재 경로가
 * 내 접근 가능한 메뉴에 없으면(=URL 직접 진입 등) 안내 후 홈으로 돌려보낸다.
 */
export function MenuAccessGuard({ children }: { children: ReactNode }) {
  const { confirmAlert } = useAlert();
  const navigate = useNavigate();

  // useCurrentMenuId 와 동일한 ['me-menus'] 쿼리를 공유(추가 호출 없음).
  const { isLoading } = useMyMenusQuery();
  const menuId = useCurrentMenuId();

  // 로딩이 끝났는데도 현재 경로에 매칭되는 메뉴가 없으면 접근 불가.
  const denied = !isLoading && menuId === null;

  useEffect(() => {
    if (!denied) return;
    confirmAlert({
      title: '이 페이지에 접근할 수 있는 권한이 없습니다.',
      icon: 'error',
      showCancelButton: false,
      onConfirm: () => navigate('/'),
    });
  }, [denied, confirmAlert, navigate]);

  if (isLoading) {
    return <Loading page />;
  }

  return denied ? null : <>{children}</>;
}
